import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { HERO_WORDS, IMAGES } from "@/data/content";
import { Wordmark } from "@/components/Wordmark";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero({ onJoin }) {
  const ref = useRef(null);
  const [wordIdx, setWordIdx] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  useEffect(() => {
    const t = setInterval(() => setWordIdx((v) => (v + 1) % HERO_WORDS.length), 2100);
    return () => clearInterval(t);
  }, []);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative h-[100svh] min-h-[640px] overflow-hidden">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster={IMAGES.hero}
          className="h-full w-full object-cover object-center"
          data-testid="hero-video"
        >
          <source src="/videos/hero.mp4" type="video/mp4" />
        </video>
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-ink/75 via-ink/45 to-ink" />
      <div className="absolute inset-0 bg-ink/25" />

      <motion.div style={{ opacity: fade }} className="relative z-10 h-full flex flex-col justify-center px-5 sm:px-10 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: -14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE }}
          className="mb-6 sm:mb-10"
        >
          <Wordmark className="text-3xl sm:text-4xl border border-zinc-700/80 px-4 py-2 inline-block bg-ink/40 backdrop-blur-sm" />
        </motion.div>

        <h1 className="font-display uppercase leading-[0.82] text-[19vw] sm:text-[15vw] lg:text-[11.5vw]">
          <MaskedLine delay={0.15}>Train</MaskedLine>
          <MaskedLine delay={0.28}>
            <span>
              For <span className="text-volt">Life.</span>
            </span>
          </MaskedLine>
        </h1>

        <div className="mt-4 sm:mt-6 font-display uppercase text-[11vw] sm:text-[7vw] lg:text-[5vw] leading-none h-[1.05em] overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.span
              key={wordIdx}
              data-testid="hero-rotating-word"
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "-100%" }}
              transition={{ duration: 0.45, ease: EASE }}
              className="block text-stroke-volt"
            >
              {HERO_WORDS[wordIdx]}
            </motion.span>
          </AnimatePresence>
        </div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6, ease: EASE }}
          className="mt-6 sm:mt-8 text-base sm:text-lg text-zinc-300 max-w-xl tracking-wide"
        >
          Not just the gym. Build strength, mobility, endurance and movement capabilities that translate into everyday life.
          <span className="block mt-3 text-sm text-zinc-500">Discover how capable your body really is — assess your Push, Pull, Squat, Bend &amp; Lift, Rotation and Endurance.</span>
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
          className="mt-8 sm:mt-10 flex flex-wrap gap-4"
          data-testid="hero-cta-row"
        >
          <a
            href="/assessment"
            data-testid="hero-assessment-button"
            className="group inline-flex items-center gap-3 bg-volt text-black uppercase tracking-[0.18em] text-xs sm:text-sm font-extrabold px-8 py-5 hover:bg-white transition-colors duration-300"
          >
            Take the Assessment
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1.5" />
          </a>
          <a
            href="#method"
            data-testid="hero-explore-button"
            className="inline-flex items-center gap-3 border border-zinc-500 text-white uppercase tracking-[0.18em] text-xs sm:text-sm font-bold px-7 py-5 hover:border-volt hover:text-volt transition-colors duration-300"
          >
            Explore Capable 360
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-3"
        data-testid="hero-scroll-indicator"
      >
        <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-400">Scroll</span>
        <div className="w-px h-14 bg-zinc-700 overflow-hidden">
          <div className="w-full h-full bg-volt animate-scroll-pulse" />
        </div>
        <ArrowDown size={14} className="text-zinc-500" />
      </motion.div>
    </section>
  );
}
